import React, { useMemo, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { MapPin } from "lucide-react";
import {
  getStoredAdminAuthContext,
  teacherStorageKeys,
} from "../../features/teacher/teacherStorage";
import { teacherApi } from "../../features/teacher/teacherApi";
import {
  TeacherBanner,
  TeacherCard,
  TeacherFeedbackMessage,
  TeacherPageShell,
} from "./TeacherUI";

const initialFormData = {
  teacher_id: "",
  address_line_1: "",
  address_line_2: "",
  city: "",
  state: "",
  country: "",
  postal_code: "",
  extra_json: "",
};

export default function AdminCreateTeacherAddress() {
  const [formData, setFormData] = useState(initialFormData);
  const [fieldErrors, setFieldErrors] = useState({});
  const [generalMessage, setGeneralMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const adminAuthContext = getStoredAdminAuthContext();
  const isAdminAuthMissing = !adminAuthContext.authToken;

  const previewPayload = useMemo(() => {
    const basePayload = {
      teacher_id: formData.teacher_id.trim(),
      address_line_1: formData.address_line_1.trim(),
      address_line_2: formData.address_line_2.trim(),
      city: formData.city.trim(),
      state: formData.state.trim(),
      country: formData.country.trim(),
      postal_code: formData.postal_code.trim(),
    };

    if (!formData.extra_json.trim()) {
      return basePayload;
    }

    try {
      return {
        ...basePayload,
        ...JSON.parse(formData.extra_json),
      };
    } catch (error) {
      return basePayload;
    }
  }, [formData]);

  const createAddressMutation = useMutation({
    mutationFn: (payload) => teacherApi.createTeacherAddress(payload, adminAuthContext),
    onSuccess: () => {
      setSuccessMessage("Teacher address created successfully.");
      setGeneralMessage("");
      setFieldErrors({});
    },
    onError: (error) => {
      setFieldErrors(error.fieldErrors || {});
      setGeneralMessage(error.message);
      setSuccessMessage("");
    },
  });

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setFieldErrors({});
    setGeneralMessage("");
    setSuccessMessage("");

    const nextErrors = {};

    if (!formData.teacher_id.trim()) {
      nextErrors.teacher_id = "teacher_id is required.";
    }

    if (!formData.address_line_1.trim()) {
      nextErrors.address_line_1 = "address_line_1 is required.";
    }

    if (formData.extra_json.trim()) {
      try {
        JSON.parse(formData.extra_json);
      } catch (error) {
        nextErrors.extra_json = "extra_json must be valid JSON.";
      }
    }

    if (Object.keys(nextErrors).length) {
      setFieldErrors(nextErrors);
      return;
    }

    if (isAdminAuthMissing) {
      setGeneralMessage(
        `Admin auth is missing. Populate localStorage.${teacherStorageKeys.adminAuthToken} from the existing admin login first.`
      );
      return;
    }

    createAddressMutation.mutate(previewPayload);
  };

  return (
    <TeacherPageShell
      pageTitle="Admin Teacher Address"
      pageSubtitle="Admin-only form for POST /api/admin_create_teacher_address/."
      email={isAdminAuthMissing ? "Admin auth missing" : "Admin auth detected"}
      headerStatus={isAdminAuthMissing ? "Needs admin token" : "Admin token ready"}
      tabs={[]}
    >
      <TeacherBanner
        title="Create Teacher Address"
        description="This page reuses local storage values from an existing admin auth flow when present. It does not create a separate admin login system."
      />

      <div className="teacher-grid teacher-grid-two">
        <TeacherCard
          title="Address Form"
          description="Common address fields are provided. Use the JSON override field if the backend expects additional keys."
        >
          {isAdminAuthMissing ? (
            <TeacherFeedbackMessage variant="info">
              TODO: wire the admin auth tokens into this app, or set localStorage keys{" "}
              <code>{teacherStorageKeys.adminAuthToken}</code> and optional{" "}
              <code>{teacherStorageKeys.adminJwt}</code> from the existing admin session.
            </TeacherFeedbackMessage>
          ) : null}
          {generalMessage ? (
            <TeacherFeedbackMessage variant="error">{generalMessage}</TeacherFeedbackMessage>
          ) : null}
          {successMessage ? (
            <TeacherFeedbackMessage variant="success">{successMessage}</TeacherFeedbackMessage>
          ) : null}

          <form className="teacher-form-grid" onSubmit={handleSubmit} noValidate>
            {[
              "teacher_id",
              "address_line_1",
              "address_line_2",
              "city",
              "state",
              "country",
              "postal_code",
            ].map((fieldName) => (
              <div
                key={fieldName}
                className={`teacher-field-group${
                  fieldName === "address_line_2" ? " teacher-form-grid-full" : ""
                }`}
              >
                <label htmlFor={`admin-address-${fieldName}`}>{fieldName}</label>
                <input
                  id={`admin-address-${fieldName}`}
                  className={`teacher-input${
                    fieldErrors[fieldName] ? " teacher-input-error" : ""
                  }`}
                  name={fieldName}
                  type="text"
                  value={formData[fieldName]}
                  onChange={handleChange}
                  placeholder={`Enter ${fieldName}`}
                />
                {fieldErrors[fieldName] ? (
                  <p className="teacher-field-error">{fieldErrors[fieldName]}</p>
                ) : null}
              </div>
            ))}

            <div className="teacher-field-group teacher-form-grid-full">
              <label htmlFor="admin-address-extra-json">extra_json</label>
              <textarea
                id="admin-address-extra-json"
                className={`teacher-textarea${
                  fieldErrors.extra_json ? " teacher-textarea-error" : ""
                }`}
                name="extra_json"
                value={formData.extra_json}
                onChange={handleChange}
                placeholder='Optional JSON object, e.g. {"is_primary": true}'
              />
              <p className="teacher-help-text">
                Use this to add backend-specific fields that were not defined in the brief.
              </p>
              {fieldErrors.extra_json ? (
                <p className="teacher-field-error">{fieldErrors.extra_json}</p>
              ) : null}
            </div>

            <div className="teacher-form-grid-full">
              <button
                className="teacher-button"
                type="submit"
                disabled={createAddressMutation.isPending}
              >
                <MapPin size={18} />
                {createAddressMutation.isPending ? "Submitting..." : "Create address"}
              </button>
            </div>
          </form>
        </TeacherCard>

        <TeacherCard
          title="Auth + Payload Preview"
          description="The endpoint expects DRF TokenAuthentication and can also consume HTTP_JWT."
        >
          <div className="teacher-grid">
            <TeacherFeedbackMessage variant={isAdminAuthMissing ? "error" : "success"}>
              {isAdminAuthMissing
                ? "Admin token is missing in local storage."
                : "Admin token found and ready to be attached as Authorization: Token <token>."}
            </TeacherFeedbackMessage>
            <TeacherFeedbackMessage variant="info">
              HTTP_JWT will be included when localStorage.adminJwt is present.
            </TeacherFeedbackMessage>
            <pre className="teacher-code-block">
              {JSON.stringify(
                {
                  authTokenPresent: Boolean(adminAuthContext.authToken),
                  jwtPresent: Boolean(adminAuthContext.jwt),
                  payload: previewPayload,
                },
                null,
                2
              )}
            </pre>
          </div>
        </TeacherCard>
      </div>
    </TeacherPageShell>
  );
}
